import { useState, useEffect, useCallback, useRef } from 'react'
import {
  Mic,
  MicOff,
  Volume2,
  VolumeX,
  X,
  ChefHat,
  Sparkles,
  HelpCircle,
  Scale,
  BookOpen,
  AlertTriangle,
  CheckCircle2
} from 'lucide-react'
import { recipesAPI } from '../lib/api'

// ══════════════════════════════════════════════════════════════════════════════
// VOICE ASSISTANT - Hands-Free Kitchen Helper
// ══════════════════════════════════════════════════════════════════════════════
//
// Truly innovative feature: A voice-activated kitchen assistant that lets chefs
// look up recipes, scale ingredients, and get cooking tips while their hands
// are busy with food prep. Uses Web Speech API for both recognition and synthesis.
//

export default function VoiceAssistant({ recipes = [], onClose }) {
  const [isListening, setIsListening] = useState(false)
  const [isSpeaking, setIsSpeaking] = useState(false)
  const [transcript, setTranscript] = useState('')
  const [response, setResponse] = useState(null)
  const [error, setError] = useState(null)
  const [commandHistory, setCommandHistory] = useState([])
  const [audioEnabled, setAudioEnabled] = useState(true)

  const recognitionRef = useRef(null)
  const synthRef = useRef(null)

  // Check for browser support
  const isSupported = typeof window !== 'undefined' &&
    ('SpeechRecognition' in window || 'webkitSpeechRecognition' in window) &&
    'speechSynthesis' in window

  // Initialize speech recognition
  useEffect(() => {
    if (!isSupported) return

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition
    recognitionRef.current = new SpeechRecognition()
    recognitionRef.current.continuous = false
    recognitionRef.current.interimResults = true
    recognitionRef.current.lang = 'en-US'

    recognitionRef.current.onresult = (event) => {
      const current = event.resultIndex
      const transcriptText = event.results[current][0].transcript
      setTranscript(transcriptText)

      if (event.results[current].isFinal) {
        processCommand(transcriptText)
      }
    }

    recognitionRef.current.onerror = (event) => {
      setError(`Voice error: ${event.error}`)
      setIsListening(false)
    }

    recognitionRef.current.onend = () => {
      setIsListening(false)
    }

    synthRef.current = window.speechSynthesis

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.abort()
      }
      if (synthRef.current) {
        synthRef.current.cancel()
      }
    }
  }, [isSupported])

  // Speak text aloud
  const speak = useCallback((text) => {
    if (!audioEnabled || !synthRef.current) return

    // Cancel any ongoing speech
    synthRef.current.cancel()

    const utterance = new SpeechSynthesisUtterance(text)
    utterance.rate = 1.0
    utterance.pitch = 1.0
    utterance.volume = 1.0

    // Try to use a natural voice
    const voices = synthRef.current.getVoices()
    const preferredVoice = voices.find(v =>
      v.name.includes('Samantha') ||
      v.name.includes('Google') ||
      v.name.includes('Natural')
    )
    if (preferredVoice) {
      utterance.voice = preferredVoice
    }

    utterance.onstart = () => setIsSpeaking(true)
    utterance.onend = () => setIsSpeaking(false)
    utterance.onerror = () => setIsSpeaking(false)

    synthRef.current.speak(utterance)
  }, [audioEnabled])

  // Process voice commands
  const processCommand = useCallback(async (text) => {
    const command = text.toLowerCase().trim()
    let result = null

    // Add to history
    setCommandHistory(prev => [...prev.slice(-4), { text, time: new Date() }])

    // COMMAND: Look up a recipe
    if (command.includes('recipe for') || command.includes('how to make') || command.includes('show me')) {
      const recipeQuery = command
        .replace('recipe for', '')
        .replace('how to make', '')
        .replace('show me', '')
        .replace('the', '')
        .trim()

      const match = recipes.find(r =>
        r.name.toLowerCase().includes(recipeQuery) ||
        recipeQuery.includes(r.name.toLowerCase().split(' ')[0])
      )

      if (match) {
        result = {
          type: 'recipe',
          recipe: match,
          message: `Found ${match.name}. Base batch is ${match.base_batch_size} ${match.base_batch_unit}. It has ${match.ingredients?.length || 0} ingredients.`
        }
        speak(result.message)
      } else {
        result = {
          type: 'error',
          message: `Sorry, I couldn't find a recipe matching "${recipeQuery}". Try saying "list recipes" to see what's available.`
        }
        speak(result.message)
      }
    }

    // COMMAND: Scale a recipe
    else if (command.includes('scale') && (command.includes('to') || command.includes('for'))) {
      const scaleMatch = command.match(/scale\s+(.+?)\s+(?:to|for)\s+(\d+(?:\.\d+)?)\s*(lbs?|pounds?|oz|gallons?)?/i)

      if (scaleMatch) {
        const [, recipeName, quantity] = scaleMatch
        const match = recipes.find(r => r.name.toLowerCase().includes(recipeName.trim()))

        if (match) {
          try {
            const scaled = await recipesAPI.scale(match.id, parseFloat(quantity))
            const topIngredients = scaled.ingredients?.slice(0, 3)
              .map(i => `${i.ingredient_name}: ${i.quantity} ${i.unit}`)
              .join('. ')

            result = {
              type: 'scaled',
              recipe: match,
              scaled,
              message: `Scaled ${match.name} to ${quantity} pounds. Scale factor is ${scaled.scale_factor}x. Top ingredients: ${topIngredients}.`
            }
            speak(result.message)
          } catch (err) {
            result = {
              type: 'error',
              message: 'Sorry, I had trouble scaling that recipe. Please try again.'
            }
            speak(result.message)
          }
        } else {
          result = {
            type: 'error',
            message: `I couldn't find a recipe called "${recipeName}".`
          }
          speak(result.message)
        }
      } else {
        result = {
          type: 'help',
          message: 'To scale a recipe, say something like "scale chimichurri to 20 pounds".'
        }
        speak(result.message)
      }
    }

    // COMMAND: List recipes
    else if (command.includes('list') && command.includes('recipe')) {
      const categories = [...new Set(recipes.map(r => r.category))].filter(Boolean)
      const recipeNames = recipes.slice(0, 5).map(r => r.name).join(', ')

      result = {
        type: 'list',
        recipes: recipes.slice(0, 10),
        message: `I have ${recipes.length} recipes across categories: ${categories.join(', ')}. Some examples: ${recipeNames}.`
      }
      speak(result.message)
    }

    // COMMAND: Read ingredients
    else if (command.includes('ingredients') || command.includes('what do i need')) {
      const recipeQuery = command
        .replace('ingredients for', '')
        .replace('what do i need for', '')
        .replace('the', '')
        .trim()

      const match = recipes.find(r =>
        r.name.toLowerCase().includes(recipeQuery) ||
        recipeQuery.includes(r.name.toLowerCase().split(' ')[0])
      )

      if (match?.ingredients?.length) {
        const ingredientList = match.ingredients
          .slice(0, 5)
          .map(i => `${i.quantity} ${i.unit} ${i.ingredient_name}`)
          .join('. ')

        result = {
          type: 'ingredients',
          recipe: match,
          message: `For ${match.name}, you need: ${ingredientList}. ${match.ingredients.length > 5 ? `And ${match.ingredients.length - 5} more ingredients.` : ''}`
        }
        speak(result.message)
      } else {
        result = {
          type: 'error',
          message: "I couldn't find that recipe. Which one did you mean?"
        }
        speak(result.message)
      }
    }

    // COMMAND: Chef notes / tips
    else if (command.includes('tips') || command.includes('chef notes') || command.includes('advice')) {
      const recipeQuery = command
        .replace('tips for', '')
        .replace('chef notes for', '')
        .replace('advice for', '')
        .replace('the', '')
        .trim()

      const match = recipes.find(r => r.name.toLowerCase().includes(recipeQuery))

      if (match?.chef_notes) {
        result = {
          type: 'tips',
          recipe: match,
          message: `Chef notes for ${match.name}: ${match.chef_notes}`
        }
        speak(result.message)
      } else {
        result = {
          type: 'error',
          message: "I don't have chef notes for that recipe."
        }
        speak(result.message)
      }
    }

    // COMMAND: Help
    else if (command.includes('help') || command.includes('what can you do')) {
      result = {
        type: 'help',
        message: 'I can help you with: "Recipe for [name]", "Scale [recipe] to [amount] pounds", "Ingredients for [recipe]", "Tips for [recipe]", or "List recipes". Just speak naturally!'
      }
      speak(result.message)
    }

    // COMMAND: Stop speaking
    else if (command.includes('stop') || command.includes('quiet') || command.includes('shut up')) {
      if (synthRef.current) {
        synthRef.current.cancel()
      }
      result = {
        type: 'status',
        message: 'Stopped.'
      }
    }

    // Unknown command
    else {
      result = {
        type: 'unknown',
        message: `I heard "${text}" but I'm not sure what you mean. Say "help" for available commands.`
      }
      speak(result.message)
    }

    setResponse(result)
  }, [recipes, speak])

  // Toggle listening
  const toggleListening = useCallback(() => {
    if (!recognitionRef.current) return

    if (isListening) {
      recognitionRef.current.abort()
      setIsListening(false)
    } else {
      setTranscript('')
      setError(null)
      recognitionRef.current.start()
      setIsListening(true)
    }
  }, [isListening])

  // Not supported
  if (!isSupported) {
    return (
      <div className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl p-8 max-w-md text-center">
          <AlertTriangle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
          <h3 className="font-display text-xl font-semibold text-havn-charcoal mb-2">
            Voice Not Supported
          </h3>
          <p className="text-havn-gray-500 mb-4">
            Your browser doesn't support voice recognition. Try Chrome or Edge for the best experience.
          </p>
          <button onClick={onClose} className="btn-primary">
            Got It
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 z-[100] bg-black/60 flex items-end sm:items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-lg overflow-hidden animate-slide-up shadow-elevated">
        {/* Header */}
        <div className="px-6 py-4 bg-gradient-to-r from-havn-green-800 to-havn-green-700 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                <ChefHat className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-display text-lg font-semibold">Kitchen Voice Assistant</h3>
                <p className="text-sm text-white/70">Hands-free recipe lookup</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setAudioEnabled(!audioEnabled)}
                className="btn-icon-white"
                aria-label={audioEnabled ? 'Mute voice responses' : 'Enable voice responses'}
              >
                {audioEnabled ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
              </button>
              <button
                onClick={onClose}
                className="btn-icon-white"
                aria-label="Close voice assistant"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="p-6">
          {/* Voice Button */}
          <div className="flex flex-col items-center mb-6">
            <button
              onClick={toggleListening}
              aria-label={isListening ? 'Stop listening' : 'Start listening'}
              aria-pressed={isListening}
              className={`w-24 h-24 rounded-full flex items-center justify-center transition-all duration-300
                ${isListening
                  ? 'bg-red-500 text-white animate-pulse shadow-lg shadow-red-200'
                  : 'bg-havn-green-700 text-white hover:bg-havn-green-600 shadow-lg shadow-havn-green-200'}`}
            >
              {isListening ? (
                <MicOff className="w-10 h-10" />
              ) : (
                <Mic className="w-10 h-10" />
              )}
            </button>
            <p className="mt-3 text-sm text-havn-gray-500">
              {isListening ? 'Listening... tap to stop' : 'Tap to speak'}
            </p>
          </div>

          {/* Transcript */}
          {transcript && (
            <div className="mb-4 p-3 bg-havn-cream-100 rounded-lg">
              <p className="text-sm text-havn-gray-500 uppercase tracking-wide mb-1">You said:</p>
              <p className="text-havn-charcoal font-medium">{transcript}</p>
            </div>
          )}

          {/* Response */}
          {response && (
            <div className={`mb-4 p-4 rounded-lg ${
              response.type === 'error' ? 'bg-red-50 border border-red-200' :
              response.type === 'help' ? 'bg-blue-50 border border-blue-200' :
              'bg-emerald-50 border border-emerald-200'
            }`}>
              <div className="flex items-start gap-3">
                {response.type === 'recipe' && <BookOpen className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />}
                {response.type === 'scaled' && <Scale className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />}
                {response.type === 'help' && <HelpCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />}
                {response.type === 'error' && <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />}
                {response.type === 'tips' && <Sparkles className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />}
                {(response.type === 'status' || response.type === 'list' || response.type === 'ingredients') &&
                  <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />}
                <div>
                  <p className="text-sm text-havn-charcoal">{response.message}</p>

                  {/* Show scaled ingredients */}
                  {response.scaled?.ingredients && (
                    <div className="mt-3 max-h-32 overflow-y-auto">
                      {response.scaled.ingredients.slice(0, 6).map((ing, i) => (
                        <div key={i} className="text-xs text-havn-gray-600 py-0.5">
                          {ing.quantity.toFixed(2)} {ing.unit} {ing.ingredient_name}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Show recipe list */}
                  {response.recipes && (
                    <div className="mt-3 flex flex-wrap gap-1">
                      {response.recipes.map((r, i) => (
                        <span key={i} className="px-2 py-0.5 bg-white text-xs rounded-full text-havn-gray-600">
                          {r.name}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {isSpeaking && (
                <div className="mt-2 flex items-center gap-2 text-xs text-havn-gray-400">
                  <Volume2 className="w-3 h-3 animate-pulse" />
                  Speaking...
                </div>
              )}
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              {error}
            </div>
          )}

          {/* Quick Commands */}
          <div className="border-t border-havn-cream-300 pt-4 mt-4">
            <p className="text-xs text-havn-gray-400 uppercase tracking-wide mb-2">Try saying:</p>
            <div className="flex flex-wrap gap-2">
              {[
                'Recipe for chimichurri',
                'Scale birria to 50 pounds',
                'Ingredients for chia base',
                'Tips for salmon',
                'List recipes'
              ].map((cmd, i) => (
                <button
                  key={i}
                  onClick={() => processCommand(cmd)}
                  className="px-3 py-1.5 bg-havn-cream-100 text-havn-gray-600 text-xs rounded-full hover:bg-havn-cream-200 transition-colors"
                >
                  "{cmd}"
                </button>
              ))}
            </div>
          </div>

          {/* Command History */}
          {commandHistory.length > 0 && (
            <div className="mt-4 pt-4 border-t border-havn-cream-300">
              <p className="text-xs text-havn-gray-400 uppercase tracking-wide mb-2">Recent commands:</p>
              <div className="space-y-1">
                {commandHistory.slice(-3).reverse().map((cmd, i) => (
                  <div key={i} className="text-xs text-havn-gray-500">
                    <span className="text-havn-gray-400">
                      {cmd.time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                    {' · '}
                    {cmd.text}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
